import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './style.module.css'
import { clsx } from 'clsx'
import { PaymentScheduleExperimentContent } from './PaymentScheduleExperimentContent'
import { ModuleContainer } from '../ModuleContainer'
import { PaymentScheduleExperimentAccordionButton } from './PaymentScheduleExperimentAccordionButton'
import { PaymentScheduleExperimentPaymentMethod } from './PaymentScheduleExperimentPaymentMethod'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TOGGLE_STATES, TrackingEvent } from '../../model/monitoring'
import { TextSizes, Text, useToast, useIsTouchScreen } from '@afterpaytouch/core'
import { setNotificationStatus, useAppDispatch, useAppRouter, useCheckoutState, useToastContext } from '../../state'
import { useTranslation } from 'next-i18next'
import { useFlag } from '@afterpaytouch/feature'
import { CheckoutStatus, Features, NotificationStatus } from '../../model'
import { FirstPaymentType } from '@afterpaytouch/portal-api'
import { useMoney } from '../Money/useMoney'

const ToastMessage: FunctionComponent<{ headingSize: TextSizes; context: string; amount: string }> = ({ headingSize, context, amount }) => {
  const { t } = useTranslation()
  return (
    <>
      <Text size={headingSize} bold testNameSpace='payment-schedule-toast-heading'>
        {t('common:toastHeading', { context, amount })}
      </Text>
      <Text size='XS' testNameSpace='payment-schedule-toast-notification'>
        {t('common:toastNotification', { context })}
      </Text>
    </>
  )
}

export const PaymentScheduleExperiment: FunctionComponent = () => {
  const classNames = clsx(styles.wrapper)
  const testNameSpace = 'payment-schedule-experiment'
  const [scheduleOpen, setToggleSchedule] = useState(false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { locale } = useAppRouter()
  const dispatch = useAppDispatch()
  const { started, firstPaymentType, status, notificationStatus } = useCheckoutState()
  const { formatMoney } = useMoney({ locale, currency: started?.installmentAmount.currency })
  const autopayToggleIsEnabled = useFlag(Features.AUTOPAY_TOGGLE_ENABLED)
  const vpufOrLpufPaymentType = firstPaymentType === FirstPaymentType.VariablePaymentUpfront || firstPaymentType === FirstPaymentType.LargePaymentUpfront
  const isVpufWithAutopayEnabled = autopayToggleIsEnabled && vpufOrLpufPaymentType
  const toast = useToast()
  const toastContext = useToastContext()
  const isTouchScreen = useIsTouchScreen()
  const npufPaymentType = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const shouldShowToast = (autopayToggleIsEnabled && vpufOrLpufPaymentType) || npufPaymentType

  const toggleSchedule = (): void => {
    const newState = !scheduleOpen
    logEvent(TrackingEvent.TOGGLED_SUMMARY_PAGE_SCHEDULE, {
      toggledValue: newState ? TOGGLE_STATES.EXPANDED : TOGGLE_STATES.COLLAPSED,
    })
    setToggleSchedule(newState)
  }

  const showToast = (): void => {
    const headingSize = isVpufWithAutopayEnabled ? 'XS' : 'L'
    dispatch(setNotificationStatus(NotificationStatus.Read))
    toast({
      message: () => <ToastMessage headingSize={headingSize} context={toastContext} amount={formatMoney(started?.amountDueToday?.amount)} />,
      kind: 'Success',
      toastTime: isVpufWithAutopayEnabled ? 0 : 5000,
      swipeToDismiss: isTouchScreen,
      testNameSpace: 'payment-schedule',
      manualDismiss: isVpufWithAutopayEnabled,
    })
  }

  useEffect(() => {
    if (status === CheckoutStatus.Started && notificationStatus === NotificationStatus.Unread) {
      if (shouldShowToast) {
        showToast()
      }
    }
  }, [firstPaymentType, notificationStatus, autopayToggleIsEnabled])

  return (
    <>
      <ModuleContainer className={classNames}>
        <div id={`${testNameSpace}-accordion`}>
          <PaymentScheduleExperimentAccordionButton onToggleSchedule={() => toggleSchedule()} scheduleOpen={scheduleOpen} />
          <PaymentScheduleExperimentContent show={scheduleOpen} />
        </div>
      </ModuleContainer>
      <PaymentScheduleExperimentPaymentMethod />
    </>
  )
}
